/**
 * Shared timeline view for episode-of-care medical record entries.
 * Used by Medical Record tab (client chart) and Service Request Tracker detail modal.
 */

import {
  ActionIcon,
  Avatar,
  Badge,
  Box,
  Button,
  Group,
  Menu,
  Paper,
  Stack,
  Text,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconDotsVertical,
  IconEdit,
  IconMapPin,
  IconPlus,
  IconTrash,
  IconVideo,
} from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import type {
  AdminNote,
  Appointment,
  ClinicalNote,
  DischargeSummary,
  Episode,
} from '../data';
import {
  isAppointmentPostSession,
  PENDING_DOCUMENTATION_LABEL,
} from '../utils/appointmentPostSession';
import { deriveAppointmentDocumentationStatus } from '../../appointments/utils/documentationStatus';

type TimelineItem =
  | { kind: 'appointment'; data: Appointment }
  | { kind: 'clinical_note'; data: ClinicalNote }
  | { kind: 'admin_note'; data: AdminNote }
  | { kind: 'discharge_summary'; data: DischargeSummary };

function formatShortDateLeft(date: Date): string {
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }).toUpperCase();
}

function getItemDate(item: TimelineItem): Date {
  if (item.kind === 'appointment') return new Date(item.data.date);
  if (item.kind === 'clinical_note') return new Date(item.data.dateOfService);
  if (item.kind === 'discharge_summary') return new Date(item.data.dischargeDate);
  return new Date(item.data.createdAt);
}

const RECORD_DATETIME_OPTIONS: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
};

function formatDateTime(isoString: string | undefined): string {
  if (!isoString) return '--';
  try {
    return new Date(isoString).toLocaleString('en-US', RECORD_DATETIME_OPTIONS);
  } catch {
    return isoString;
  }
}

function formatRecordDateTime(dateString: string, timeString?: string): string {
  if (!dateString) return '--';
  try {
    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) return dateString;
    const datePart = d.toLocaleDateString('en-US', {
      month: RECORD_DATETIME_OPTIONS.month,
      day: RECORD_DATETIME_OPTIONS.day,
      year: RECORD_DATETIME_OPTIONS.year,
    });
    if (timeString?.trim()) return `${datePart}, ${timeString.trim()}`;
    return d.toLocaleString('en-US', RECORD_DATETIME_OPTIONS);
  } catch {
    return dateString;
  }
}

function getNoteEpisodeId(note: ClinicalNote, appointments: Appointment[]): string | undefined {
  if (note.appointmentId) {
    const apt = appointments.find((a) => a.id === note.appointmentId);
    return apt?.episodeId;
  }
  return undefined;
}

function getProviderInitials(name: string): string {
  const parts = name.replace(/\s+(MD|PhD|PsyD|LCSW|LMFT)\.?$/i, '').trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] ?? '') + (parts[parts.length - 1][0] ?? '');
  return name.slice(0, 2).toUpperCase();
}

export interface EpisodeTimelineViewProps {
  /** Minimal client for labels and navigation */
  client: { id: string; name: string; primaryProvider?: string; provider?: string };
  appointments: Appointment[];
  notes: ClinicalNote[];
  adminNotes: AdminNote[];
  episodes: Episode[];
  dischargeSummaries: DischargeSummary[];
  /** When set, only show entries for this episode of care */
  episodeId?: string;
  /** Optional date range filter [start, end] */
  dateRangeValue?: [Date | null, Date | null];
  /** Optional provider filter (when provided, only these providers' appointments shown) */
  providerFilter?: string[];
  /** Optional status filter (when provided, only appointments with these statuses; when active, non-appointment entries are hidden) */
  statusFilter?: string[];
  /** Show "Add note" button (e.g. hide in embedded modal) */
  showAddNote?: boolean;
  onOpenAppointment?: (apt: Appointment) => void;
  onViewNote?: (note: ClinicalNote) => void;
  onOpenAddNote?: (type: 'chart' | 'admin') => void;
  onOpenOtherDocument?: () => void;
  onEditAdminNote?: (adminNote: AdminNote) => void;
  onDeleteAdminNote?: (adminNote: AdminNote) => void;
}

export function EpisodeTimelineView({
  client: _client,
  appointments,
  notes,
  adminNotes,
  episodes,
  dischargeSummaries,
  episodeId,
  dateRangeValue = [null, null],
  providerFilter,
  statusFilter,
  showAddNote = true,
  onOpenAppointment,
  onViewNote,
  onOpenAddNote,
  onOpenOtherDocument,
  onEditAdminNote,
  onDeleteAdminNote,
}: EpisodeTimelineViewProps): JSX.Element {
  const [expandedRecordId, setExpandedRecordId] = useState<string | null>(null);

  const notesByAppointmentId = useMemo(() => {
    const map = new Map<string, ClinicalNote>();
    notes.forEach((note) => {
      if (note.appointmentId) map.set(note.appointmentId, note);
    });
    return map;
  }, [notes]);

  const notesById = useMemo(() => {
    const map = new Map<string, ClinicalNote>();
    notes.forEach((note) => map.set(note.id, note));
    return map;
  }, [notes]);

  const getEpisodeName = (episodeId: string | undefined): string => {
    if (!episodeId) return '—';
    const ep = episodes.find((e) => e.id === episodeId);
    return ep?.name ?? '—';
  };

  const filteredAppointments = useMemo(() => {
    let list = [...appointments];
    if (episodeId) list = list.filter((a) => a.episodeId === episodeId);
    if (providerFilter && providerFilter.length > 0)
      list = list.filter((a) => providerFilter.includes(a.provider));
    if (statusFilter && statusFilter.length > 0)
      list = list.filter((a) => statusFilter.includes(a.status));
    return list.sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
    );
  }, [appointments, episodeId, providerFilter, statusFilter]);

  const otherNotes = useMemo(() => notes.filter((n) => !n.appointmentId), [notes]);

  const filteredOtherNotes = useMemo(() => {
    if (!episodeId) return otherNotes;
    return otherNotes.filter((n) => {
      const eid = getNoteEpisodeId(n, appointments);
      return eid != null && eid === episodeId;
    });
  }, [otherNotes, episodeId, appointments]);

  const adminNotesWithNoteId = useMemo(
    () => adminNotes.filter((a) => a.noteId),
    [adminNotes]
  );

  const filteredAdminNotesLinked = useMemo(() => {
    if (!episodeId) return adminNotesWithNoteId;
    return adminNotesWithNoteId.filter((a) => {
      const note = a.noteId ? notesById.get(a.noteId) : undefined;
      const eid = note ? getNoteEpisodeId(note, appointments) : undefined;
      return eid != null && eid === episodeId;
    });
  }, [adminNotesWithNoteId, episodeId, notesById, appointments]);

  const otherAdminNotes = useMemo(() => {
    const standalone = adminNotes.filter((a) => !a.noteId);
    if (!episodeId) return standalone;
    return standalone.filter((a) => !a.episodeId || a.episodeId === episodeId);
  }, [adminNotes, episodeId]);

  const filteredDischargeSummaries = useMemo(() => {
    if (!episodeId) return dischargeSummaries;
    return dischargeSummaries.filter((ds) => ds.episodeId === episodeId);
  }, [dischargeSummaries, episodeId]);

  const statusFilterActive = Boolean(statusFilter && statusFilter.length > 0);

  const timelineItems = useMemo((): TimelineItem[] => {
    const items: TimelineItem[] = [
      ...filteredAppointments.map((a) => ({ kind: 'appointment' as const, data: a })),
      ...(statusFilterActive
        ? []
        : [
            ...filteredOtherNotes.map((n) => ({ kind: 'clinical_note' as const, data: n })),
            ...filteredAdminNotesLinked.map((a) => ({ kind: 'admin_note' as const, data: a })),
            ...otherAdminNotes.map((a) => ({ kind: 'admin_note' as const, data: a })),
            ...filteredDischargeSummaries.map((d) => ({
              kind: 'discharge_summary' as const,
              data: d,
            })),
          ]),
    ];
    const getSortTime = (item: TimelineItem): number => {
      if (item.kind === 'appointment') return new Date(item.data.date).getTime();
      if (item.kind === 'clinical_note') return new Date(item.data.dateOfService).getTime();
      if (item.kind === 'discharge_summary') return new Date(item.data.dischargeDate).getTime();
      return new Date(item.data.createdAt).getTime();
    };
    return items.sort((a, b) => getSortTime(b) - getSortTime(a));
  }, [
    filteredAppointments,
    filteredOtherNotes,
    filteredAdminNotesLinked,
    otherAdminNotes,
    filteredDischargeSummaries,
    statusFilterActive,
  ]);

  const filteredByDateRange = useMemo(() => {
    const [startVal, endVal] = dateRangeValue;
    if (!startVal && !endVal) return timelineItems;
    const start = startVal ? new Date(startVal).getTime() : null;
    const end = endVal ? new Date(endVal).getTime() : null;
    return timelineItems.filter((item) => {
      const t = getItemDate(item).getTime();
      if (start !== null && t < start) return false;
      if (end !== null && t > end) return false;
      return true;
    });
  }, [timelineItems, dateRangeValue]);

  const timelineByYear = useMemo(() => {
    const map = new Map<number, TimelineItem[]>();
    for (const item of filteredByDateRange) {
      const year = getItemDate(item).getFullYear();
      const list = map.get(year) ?? [];
      list.push(item);
      map.set(year, list);
    }
    return Array.from(map.entries())
      .sort(([a], [b]) => b - a)
      .map(([year, items]) => ({ year, items }));
  }, [filteredByDateRange]);

  const addNoteMenu = showAddNote && onOpenAddNote && (
    <Menu shadow="md" width={200} position="bottom-end">
      <Menu.Target>
        <Button leftSection={<IconPlus size={16} />} variant="light" size="sm">
          Add note
        </Button>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Item onClick={() => onOpenAddNote('chart')}>Chart note</Menu.Item>
        <Menu.Item onClick={() => onOpenAddNote('admin')}>Admin note</Menu.Item>
        {onOpenOtherDocument && (
          <Menu.Item onClick={onOpenOtherDocument}>Add other document</Menu.Item>
        )}
      </Menu.Dropdown>
    </Menu>
  );

  return (
    <Paper p="lg" withBorder>
      <Stack gap="md">
        {filteredByDateRange.length === 0 ? (
          <Text c="gray.6" ta="center" py="xl">
            No chart entries
          </Text>
        ) : (
          <Stack gap={0}>
            {timelineByYear.map(({ year, items }, yearIndex) => (
              <Box key={year}>
                <Box py="xs" mb="sm" style={{ borderBottom: '1px solid var(--mantine-color-gray-3)' }}>
                  <Group justify="space-between" align="center">
                    <Box style={{ width: 80, flexShrink: 0, textAlign: 'center' }}>
                      <Text size="lg" fw={600} c="gray.7">
                        {year}
                      </Text>
                    </Box>
                    {yearIndex === 0 ? addNoteMenu : null}
                  </Group>
                </Box>
                <Stack gap="md">
                  {items.map((item) => {
                    const itemDate = getItemDate(item);
                    const shortDate = formatShortDateLeft(itemDate);
                    if (item.kind === 'appointment') {
                      const apt = item.data;
                      const linkedNote = notesByAppointmentId.get(apt.id);
                      const isTelehealth = apt.location.type === 'Telehealth';
                      const locationLabel = isTelehealth
                        ? (apt.location.platform || 'Telehealth')
                        : 'In Person';
                      const episodeName = getEpisodeName(apt.episodeId);
                      const lastModifiedDisplay = linkedNote
                        ? formatDateTime(linkedNote.lastModified ?? linkedNote.signedDate)
                        : formatRecordDateTime(apt.date, apt.time);
                      return (
                        <Group
                          key={`apt-${apt.id}`}
                          wrap="nowrap"
                          align="flex-start"
                          gap="md"
                        >
                          <Badge
                            variant="light"
                            color="gray"
                            size="lg"
                            style={{ width: 80, flexShrink: 0 }}
                          >
                            {shortDate}
                          </Badge>
                          <Paper
                            p="md"
                            withBorder
                            bg="gray.0"
                            style={{
                              cursor: onOpenAppointment ? 'pointer' : undefined,
                              flex: 1,
                              minWidth: 0,
                            }}
                            onClick={() => onOpenAppointment?.(apt)}
                          >
                            <Group
                              justify="space-between"
                              mb="xs"
                              wrap="nowrap"
                              align="center"
                            >
                              <Group gap="sm" wrap="nowrap">
                                <Avatar color="gray" radius="xl" size="sm">
                                  {getProviderInitials(apt.provider)}
                                </Avatar>
                                <div>
                                  <Group gap="xs" wrap="wrap" align="center">
                                    <Text size="sm" fw={500}>
                                      {apt.serviceType}
                                    </Text>
                                    {apt.status !== 'Completed' && (
                                      <Badge
                                        color={
                                          apt.status === 'No-Show'
                                            ? 'red'
                                            : apt.status === 'Cancelled'
                                              ? 'gray'
                                              : 'blue'
                                        }
                                        variant="light"
                                        size="sm"
                                      >
                                        {apt.status}
                                      </Badge>
                                    )}
                                    {apt.status === 'Completed' && !apt.hasNote && isAppointmentPostSession(apt) && (
                                      <Badge
                                        color="orange"
                                        variant="filled"
                                        size="sm"
                                        leftSection={<IconAlertCircle size={14} />}
                                        styles={{
                                          root: {
                                            boxShadow:
                                              '0 0 0 2px var(--mantine-color-orange-3), 0 2px 8px rgba(234, 88, 12, 0.35)',
                                            fontWeight: 600,
                                          },
                                        }}
                                      >
                                        {PENDING_DOCUMENTATION_LABEL}
                                      </Badge>
                                    )}
                                    {apt.status === 'Completed' && apt.hasNote && (() => {
                                      const ln = linkedNote;
                                      const doc = deriveAppointmentDocumentationStatus(apt, ln);
                                      if (doc === 'Pending Cosignature' || ln?.noteStatus === 'Pending co-signature') {
                                        return <Badge color="orange" variant="light" size="sm">Pending Cosignature</Badge>;
                                      }
                                      if (doc === 'Signed' || ln?.noteStatus === 'Signed') {
                                        return <Badge color="blue" variant="light" size="sm">Signed</Badge>;
                                      }
                                      if (doc === 'Pending Signature') {
                                        return <Badge color="yellow" variant="light" size="sm">Pending Signature</Badge>;
                                      }
                                      return null;
                                    })()}
                                  </Group>
                                  <Text size="xs" c="gray.6">
                                    {apt.provider}
                                  </Text>
                                </div>
                              </Group>
                              <Text size="xs" c="gray.6" title="Last modified">
                                {lastModifiedDisplay}
                              </Text>
                            </Group>
                            <Group gap="xs" align="flex-start" wrap="nowrap">
                              {isTelehealth ? (
                                <IconVideo
                                  size={14}
                                  style={{ color: 'var(--mantine-color-blue-6)', marginTop: 2 }}
                                />
                              ) : (
                                <IconMapPin
                                  size={14}
                                  style={{ color: 'var(--mantine-color-gray-6)', marginTop: 2 }}
                                />
                              )}
                              <Text size="sm" c="gray.7">
                                {locationLabel}
                              </Text>
                              <Text size="sm" c="gray.5">
                                •
                              </Text>
                              <Text size="sm" c="gray.6">
                                {episodeName}
                              </Text>
                            </Group>
                            {apt.notes && (
                              <Text size="sm" c="gray.7" mt="xs">
                                {apt.notes}
                              </Text>
                            )}
                            {apt.hasNote && linkedNote?.soapNote && (
                              <>
                                {expandedRecordId === `apt-${apt.id}` ? (
                                  <Stack
                                    gap="xs"
                                    mt="sm"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <Text size="sm" fw={600} c="gray.7">
                                      Subjective
                                    </Text>
                                    <Text size="sm" c="gray.7">
                                      {linkedNote.soapNote.subjective}
                                    </Text>
                                    <Text size="sm" fw={600} c="gray.7">
                                      Objective
                                    </Text>
                                    <Text size="sm" c="gray.7">
                                      {linkedNote.soapNote.objective}
                                    </Text>
                                    <Text size="sm" fw={600} c="gray.7">
                                      Assessment
                                    </Text>
                                    <Text size="sm" c="gray.7">
                                      {linkedNote.soapNote.assessment}
                                    </Text>
                                    <Text size="sm" fw={600} c="gray.7">
                                      Plan
                                    </Text>
                                    <Text size="sm" c="gray.7">
                                      {linkedNote.soapNote.plan}
                                    </Text>
                                    <Button
                                      variant="subtle"
                                      size="xs"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setExpandedRecordId(null);
                                      }}
                                    >
                                      Show less
                                    </Button>
                                  </Stack>
                                ) : (
                                  <Button
                                    variant="subtle"
                                    size="xs"
                                    mt="xs"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setExpandedRecordId(`apt-${apt.id}`);
                                    }}
                                  >
                                    Show more
                                  </Button>
                                )}
                              </>
                            )}
                          </Paper>
                        </Group>
                      );
                    }
                    if (item.kind === 'clinical_note') {
                      const note = item.data;
                      const description =
                        note.soapNote?.subjective ||
                        note.title ||
                        `${note.noteType} — ${note.serviceRendered || note.serviceType}`;
                      const noteLastModified = formatDateTime(
                        note.lastModified ?? note.signedDate
                      );
                      return (
                        <Group
                          key={`note-${note.id}`}
                          wrap="nowrap"
                          align="flex-start"
                          gap="md"
                        >
                          <Badge
                            variant="light"
                            color="gray"
                            size="lg"
                            style={{ width: 80, flexShrink: 0 }}
                          >
                            {shortDate}
                          </Badge>
                          <Paper
                            p="md"
                            withBorder
                            bg="gray.0"
                            style={{
                              cursor: onViewNote ? 'pointer' : undefined,
                              flex: 1,
                              minWidth: 0,
                            }}
                            onClick={() => onViewNote?.(note)}
                          >
                            <Group
                              justify="space-between"
                              mb="xs"
                              wrap="nowrap"
                              align="center"
                            >
                              <Group gap="sm" wrap="nowrap">
                                <Avatar color="gray" radius="xl" size="sm">
                                  {getProviderInitials(note.provider)}
                                </Avatar>
                                <div>
                                  <Group gap="xs" wrap="wrap" align="center">
                                    <Text size="sm" fw={500}>
                                      {note.title}
                                    </Text>
                                    {note.noteStatus === 'Signed' && (
                                      <Badge color="blue" variant="light" size="sm">
                                        Signed
                                      </Badge>
                                    )}
                                    {note.noteStatus === 'Complete' && (
                                      <Badge color="teal" variant="light" size="sm">
                                        Completed
                                      </Badge>
                                    )}
                                  </Group>
                                  <Text size="xs" c="gray.6">
                                    {note.provider}
                                  </Text>
                                </div>
                              </Group>
                              <Text size="xs" c="gray.6" title="Last modified">
                                {noteLastModified}
                              </Text>
                            </Group>
                            <Text size="sm" c="gray.7">
                              {description}
                            </Text>
                            {note.soapNote &&
                              (expandedRecordId === `note-${note.id}` ? (
                                <Stack
                                  gap="xs"
                                  mt="sm"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <Text size="sm" fw={600} c="gray.7">
                                    Subjective
                                  </Text>
                                  <Text size="sm" c="gray.7">
                                    {note.soapNote.subjective}
                                  </Text>
                                  <Text size="sm" fw={600} c="gray.7">
                                    Objective
                                  </Text>
                                  <Text size="sm" c="gray.7">
                                    {note.soapNote.objective}
                                  </Text>
                                  <Text size="sm" fw={600} c="gray.7">
                                    Assessment
                                  </Text>
                                  <Text size="sm" c="gray.7">
                                    {note.soapNote.assessment}
                                  </Text>
                                  <Text size="sm" fw={600} c="gray.7">
                                    Plan
                                  </Text>
                                  <Text size="sm" c="gray.7">
                                    {note.soapNote.plan}
                                  </Text>
                                  <Button
                                    variant="subtle"
                                    size="xs"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setExpandedRecordId(null);
                                    }}
                                  >
                                    Show less
                                  </Button>
                                </Stack>
                              ) : (
                                <Button
                                  variant="subtle"
                                  size="xs"
                                  mt="xs"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setExpandedRecordId(`note-${note.id}`);
                                  }}
                                >
                                  Show more
                                </Button>
                              ))}
                          </Paper>
                        </Group>
                      );
                    }
                    if (item.kind === 'discharge_summary') {
                      const ds = item.data;
                      const episode = episodes.find((e) => e.id === ds.episodeId);
                      const fullText = [ds.reasonForDischarge, ds.summaryOfCare, ds.recommendations]
                        .filter(Boolean)
                        .join('\n\n');
                      const isExpanded = expandedRecordId === `ds-${ds.id}`;
                      return (
                        <Group
                          key={`ds-${ds.id}`}
                          wrap="nowrap"
                          align="flex-start"
                          gap="md"
                        >
                          <Badge
                            variant="light"
                            color="gray"
                            size="lg"
                            style={{ width: 80, flexShrink: 0 }}
                          >
                            {shortDate}
                          </Badge>
                          <Paper
                            p="md"
                            withBorder
                            bg="gray.0"
                            style={{ flex: 1, minWidth: 0 }}
                          >
                            <Group justify="space-between" mb="xs" wrap="nowrap">
                              <Text size="sm" fw={500}>
                                Discharge summary — {episode?.name ?? 'Episode'}
                              </Text>
                              <Text size="xs" c="gray.6">
                                {formatDateTime(ds.signedAt)}
                              </Text>
                            </Group>
                            <Box
                              style={{
                                maxHeight: isExpanded ? 'none' : 80,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              <Text
                                size="sm"
                                c="gray.7"
                                style={{ whiteSpace: 'pre-wrap', margin: 0 }}
                              >
                                {fullText}
                              </Text>
                            </Box>
                            {fullText.length > 120 && (
                              <Button
                                variant="subtle"
                                size="xs"
                                mt="xs"
                                onClick={() =>
                                  setExpandedRecordId(isExpanded ? null : `ds-${ds.id}`)
                                }
                              >
                                {isExpanded ? 'Show less' : 'Show more'}
                              </Button>
                            )}
                          </Paper>
                        </Group>
                      );
                    }
                    const adminNote = item.data;
                    const adminLastModified = formatDateTime(
                      adminNote.updatedAt ?? adminNote.createdAt
                    );
                    const hasAdminActions = onEditAdminNote || onDeleteAdminNote;
                    return (
                      <Group
                        key={`admin-${adminNote.id}`}
                        wrap="nowrap"
                        align="flex-start"
                        gap="md"
                      >
                        <Badge
                          variant="light"
                          color="gray"
                          size="lg"
                          style={{ width: 80, flexShrink: 0 }}
                        >
                          {shortDate}
                        </Badge>
                        <Paper
                          p="md"
                          withBorder
                          bg="gray.0"
                          style={{ flex: 1, minWidth: 0 }}
                        >
                          <Group justify="space-between" mb="xs" wrap="nowrap">
                            <Text size="sm" fw={500}>
                              {adminNote.title ?? adminNote.type}
                            </Text>
                            <Group gap="xs" wrap="nowrap">
                              <Text size="xs" c="gray.6" title="Last modified">
                                {adminLastModified}
                              </Text>
                              {hasAdminActions && (
                                <Menu position="bottom-end" withArrow>
                                  <Menu.Target>
                                    <ActionIcon
                                      variant="subtle"
                                      size="sm"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <IconDotsVertical size={14} />
                                    </ActionIcon>
                                  </Menu.Target>
                                  <Menu.Dropdown>
                                    {onEditAdminNote && (
                                      <Menu.Item
                                        leftSection={<IconEdit size={14} />}
                                        onClick={() => onEditAdminNote(adminNote)}
                                      >
                                        Edit
                                      </Menu.Item>
                                    )}
                                    {onDeleteAdminNote && (
                                      <Menu.Item
                                        leftSection={<IconTrash size={14} />}
                                        color="red"
                                        onClick={() => onDeleteAdminNote(adminNote)}
                                      >
                                        Delete
                                      </Menu.Item>
                                    )}
                                  </Menu.Dropdown>
                                </Menu>
                              )}
                            </Group>
                          </Group>
                          <Text size="sm" c="gray.7">
                            {adminNote.content}
                          </Text>
                        </Paper>
                      </Group>
                    );
                  })}
                </Stack>
              </Box>
            ))}
          </Stack>
        )}

        <Text size="sm" c="gray.6">
          Showing {filteredByDateRange.length} entries
        </Text>
      </Stack>
    </Paper>
  );
}
